import { Component, ElementRef, ViewChild, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';
import {
  DataTableComponent,
  TableCellDirective,
  TableColumn,
  TableHeaderDirective,
  TableRowDetailDirective,
} from '@libs/front/components/data-table/data-table.component';
import { SelectComponent, SelectOption as LibSelectOption } from '@libs/front/components/select/select.component';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { CheckboxComponent } from '@libs/front/components/checkbox/checkbox.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { CalendarComponent } from '@libs/front/components/calendar/calendar.component';

import { BlastVibrationChartComponent } from './vibration-chart/blast-vibration-chart.component';
import { BlastRecordsAssignmentComponent } from './records-assignment/blast-records-assignment.component';
import { BlastAssignRecordsModalComponent } from './assign-records-modal/blast-assign-records-modal.component';
import { BlastRecordModalComponent } from './record-modal/blast-record-modal.component';

import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { PonderationService } from 'src/app/services/ponderation.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { BlastService } from 'src/app/services/blast.service';
import { Tir } from 'src/app/bo/tir';

import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { VibrationRecord } from 'src/app/bo/vibrationsForChart/vibration-record';
import { ControlPoint } from 'src/app/bo/controlPoint';
import { Ponderation } from 'src/app/bo/ponderation.model';
import { environment } from 'src/environments/environment';
import { toPreviewDataUrl } from './vibration-preview.util';

interface VibrationSparkline {
  points: string;
  max: number;
  min: number;
  ticks: string[];
}

type AssignmentFilter = 'all' | 'assigned' | 'unassigned';

/** Un enregistrement ne dépasse jamais 4 canaux (3 axes de géophone + 1 micro). */
const MAX_CHANNELS = 4;

interface VibrationRow {
  id: number;
  record: VibrationData;
  timestampMs: number;
  dateLabel: string;
  timeLabel: string;
  vAbs: number | null;
  lAbs: number | null;
  tAbs: number | null;
  vecSomme: number | null;
  overpressureDb: number | null;
  /** PPV max des trois axes — clé de tri de la colonne "Vibrations". */
  maxPpv: number;
  /** Notes concaténées — clé de tri de la colonne "Notes". */
  notes: string;
  note1: string;
  note2: string;
  note3: string;
  deviceLabel: string;
  controlPointName: string;
  /** Aperçus pré-rendus par le back, indexés par canal (data URL ou null). */
  previews: (string | null)[];
}

@Component({
  selector: 'app-blast-vibration',
  templateUrl: './blast-vibration.component.html',
  styleUrls: ['./blast-vibration.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ScrollableComponent,
    DataTableComponent,
    TableCellDirective,
    TableHeaderDirective,
    TableRowDetailDirective,
    SelectComponent,
    ButtonComponent,
    CheckboxComponent,
    PopupComponent,
    InputComponent,
    CalendarComponent,
    BlastVibrationChartComponent,
    BlastRecordsAssignmentComponent,
    BlastAssignRecordsModalComponent,
    BlastRecordModalComponent,
  ],
})
export class BlastVibrationComponent implements OnInit {
  readonly blastId = input.required<number>();

  @ViewChild('tabsTrack') private readonly tabsTrack?: ElementRef<HTMLElement>;

  private readonly vibrationsRecordService = inject(VibrationsRecordService);
  private readonly utilisateurService = inject(UtilisateurService);
  private readonly ponderationService = inject(PonderationService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly blastService = inject(BlastService);
  private readonly translate = inject(TranslateService);
  private readonly cookieService = inject(CookieService);

  private readonly vibradataUrl = environment.vibradataFront;

  protected readonly tirName = signal('');
  /** Date de tir réelle, sinon planifiée */
  protected readonly tirDate = signal<Date | null>(null);
  protected readonly controlPoints = signal<ControlPoint[]>([]);
  protected readonly records = signal<VibrationData[]>([]);
  protected readonly ponderations = signal<Ponderation[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly hasLoaded = signal(false);

  /** null = pas de filtre de date (affiche tous les enregistrements du blast) */
  protected readonly selectedDate = signal<string | null>(null);
  protected readonly assignmentFilter = signal<AssignmentFilter>('all');
  protected readonly activeControlPointId = signal<number | null>(null);
  protected readonly searchTerm = signal('');
  protected readonly selectedChannelIndex = signal(0);

  protected readonly selectedIds = signal<Set<number>>(new Set());
  protected readonly expandedRowId = signal<number | null>(null);
  protected readonly noteEditRowId = signal<number | null>(null);
  protected readonly noteDraft = signal({ note1: '', note2: '', note3: '' });
  protected readonly distanceEditRowId = signal<number | null>(null);
  protected readonly distanceDraft = signal<number | null>(null);
  protected readonly pendingDeleteIds = signal<number[] | null>(null);
  protected readonly multiAssignOpen = signal(false);
  protected readonly addRecordOpen = signal(false);

  protected readonly siteId = computed(() => this.utilisateurService.currentSite()?.siteId);
  protected readonly siteName = computed(() => this.utilisateurService.currentSite()?.nom ?? '');
  protected readonly regionName = computed(() => this.utilisateurService.currentSite()?.region?.nom);

  protected readonly viewState = computed<'loading' | 'content' | 'empty'>(() => {
    if (this.isLoading() || !this.hasLoaded()) return 'loading';
    return this.records().length ? 'content' : 'empty';
  });

  protected readonly assignedControlPoints = computed<ControlPoint[]>(() => {
    const records = this.records();
    const cps = this.controlPoints();
    if (!records.length || !cps.length) return [];
    const ids = new Set(records.filter(r => r.controlPointId).map(r => r.controlPointId));
    return cps.filter(cp => ids.has(cp.controlPointId));
  });

  /**
   * Colonnes du tableau générique. Les clés correspondent à des propriétés plates de VibrationRow
   * pour que le tri natif du composant fonctionne ; le rendu passe par les <ng-template tableCell>.
   */
  protected readonly tableColumns: TableColumn[] = [
    { key: 'select', label: '', sortable: false, width: '44px' },
    { key: 'channel', label: 'RECORD.CHANNEL', sortable: false},
    { key: 'timestampMs', label: 'VIBRATION.TIMESTAMP'},
    { key: 'maxPpv', label: 'VIBRATION.VIBRATIONS_MM_S'},
    { key: 'notes', label: 'VIBRATION.NOTES' },
    { key: 'controlPointName', label: 'VIBRATION.ASSIGNMENT', width: '215px' },
    { key: 'expand', label: '', sortable: false, width: '44px' },
  ];

  protected readonly assignmentOptions = computed<LibSelectOption[]>(() => [
    { id: 'all', label: this.translate.instant('VIBRATION.ALL') },
    { id: 'assigned', label: this.translate.instant('MESSAGE_INFO.ASSIGNED_RECORDS') },
    { id: 'unassigned', label: this.translate.instant('MESSAGE_INFO.UNASSIGNED_RECORDS') },
  ]);

  protected readonly rows = computed<VibrationRow[]>(() => {
    const cps = this.controlPoints();
    const term = this.searchTerm().trim().toLowerCase();
    const assignment = this.assignmentFilter();
    const mapped = this.records().map(record => this.toRow(record, cps));
    const byAssignment = assignment === 'all'
      ? mapped
      : mapped.filter(row => (assignment === 'assigned') === (row.record.controlPointId != null && row.record.controlPointId !== 0));
    const filtered = term ? byAssignment.filter(row => this.matchesSearch(row, term)) : byAssignment;
    console.log(filtered);
    
    return filtered.slice().sort((a, b) => b.timestampMs - a.timestampMs);
  });

  protected readonly maxChannelCount = computed(() => this.rows().reduce((max, r) => Math.max(max, this.channelCount(r)), 1));

  protected readonly allSelected = computed(() => {
    const rows = this.rows();
    return rows.length > 0 && rows.every(r => this.selectedIds().has(r.id));
  });

  protected readonly selectedCount = computed(() => this.selectedIds().size);
  protected readonly selectedRows = computed(() => this.rows().filter(r => this.selectedIds().has(r.id)));
  protected readonly canBulkReport = computed(() => this.selectedCount() === 1);

  constructor() {
    effect(() => {
      const id = this.blastId();
      if (!id) return;
      this.selectedIds.set(new Set());
      this.expandedRowId.set(null);
      this.activeControlPointId.set(null);
      this.hasLoaded.set(false);
      this.loadTirName(id);
      this.loadControlPoints();
    });

    effect(() => {
      const id = this.blastId();
      const date = this.selectedDate();
      const controlPointId = this.activeControlPointId();
      const siteId = this.siteId();
      if (!id || !siteId) return;
      this.loadRecords(siteId, id, controlPointId, null, date);
    });
  }

  ngOnInit(): void {
    this.loadPonderations();
  }

  // ── Chargement ─────────────────────────────────────────────────────────────

  private loadTirName(blastId: number): void {
    this.blastService.getBlast(blastId).subscribe({
      next: (tir) => {
        this.tirName.set(tir?.nom ?? '');
        this.tirDate.set(Tir.GetBestDate(tir) ?? null);
      },
    });
  }

  private loadControlPoints(): void {
    const siteId = this.siteId();
    if (!siteId) return;
    this.vibrationsRecordService.loadControlPoints(siteId).subscribe({
      next: (cps) => this.controlPoints.set(cps ?? []),
      error: () => {
        this.controlPoints.set([]);
        this.fonctionsService.launch_toast(false, this.translate.instant('ERROR.LOAD_FAILED'));
      },
    });
  }

  private loadPonderations(): void {
    this.ponderationService.getAllPonderations().subscribe({
      next: (response) => this.ponderations.set(response.data ?? []),
    });
  }

  private loadRecords(siteId: number, blastId: number, controlPointId: number | null, device: string | null, dateStr: string | null): void {
    this.isLoading.set(true);
    const startDate = dateStr ? `${dateStr}T00:00:00` : null;
    const endDate = dateStr ? `${dateStr}T23:59:59` : null;
    console.log('get');
    
    // includeSignal=false : demander le signal complet de tous les enregistrements en une seule
    // requête faisait planter l'API (réponse chunked tronquée, ERR_INCOMPLETE_CHUNKED_ENCODING)
    // dès qu'une journée contenait plusieurs dizaines de records. Le signal est donc chargé au
    // cas par cas après coup, cf. loadSparklinesProgressively().
    this.vibrationsRecordService
      .GetRecords(siteId, controlPointId, blastId, device, 1, 200, false, false, false, startDate, endDate)
      .subscribe({
        next: (response) => {
          const records = response.data?.records ?? [];
          this.records.set(records);
          console.log(this.records());

          this.isLoading.set(false);
          this.hasLoaded.set(true);
          this.loadSparklinesProgressively(siteId, records);
        },
        error: () => {
          this.records.set([]);
          this.isLoading.set(false);
          this.hasLoaded.set(true);
        },
      });
  }

  /**
   * Charge le signal complet des enregistrements un par un (concurrence limitée), pour alimenter
   * les mini-graphiques par ligne sans reproduire le payload trop lourd d'un chargement en masse.
   */
  private loadSparklinesProgressively(siteId: number, records: VibrationData[]): void {
    const queue = [...records];
    const concurrency = 4;

    const next = (): void => {
      const record = queue.shift();
      if (!record) return;
      if (record.signalDataEntity) { next(); return; }
      this.vibrationsRecordService.GetRecordById(siteId, record.id).subscribe({
        next: (response) => {
          record.signalDataEntity = response.data.signalDataEntity;
          record.timeSpectrumWeighted = response.data.timeSpectrumWeighted;
          this.records.set([...this.records()]);
          next();
        },
        error: () => next(),
      });
    };

    for (let i = 0; i < concurrency; i++) next();
  }

  protected reload(): void {
    const siteId = this.siteId();
    if (!siteId) return;
    this.loadRecords(siteId, this.blastId(), this.activeControlPointId(), null, this.selectedDate());
  }

  // ── View model ────────────────────────────────────────────────────────────

  private toRow(record: VibrationData, cps: ControlPoint[]): VibrationRow {
    const meta = record.recordMetadata;
    const controlPoint = cps.find(cp => cp.controlPointId === record.controlPointId);
    const [dateLabel, timeLabel] = this.formatTimestamp(meta?.timestamp);

    return {
      id: record.id,
      record,
      timestampMs: meta?.timestamp ? new Date(meta.timestamp).getTime() : 0,
      dateLabel,
      timeLabel,
      vAbs: meta?.verticalPpv != null ? Math.abs(meta.verticalPpv) : null,
      lAbs: meta?.longitudinalPpv != null ? Math.abs(meta.longitudinalPpv) : null,
      tAbs: meta?.transversePpv != null ? Math.abs(meta.transversePpv) : null,
      vecSomme: meta?.vecSomme ?? null,
      overpressureDb: meta?.overpressure != null ? this.pascalToDecibel(meta.overpressure) : null,
      maxPpv: Math.max(
        meta?.verticalPpv != null ? Math.abs(meta.verticalPpv) : 0,
        meta?.longitudinalPpv != null ? Math.abs(meta.longitudinalPpv) : 0,
        meta?.transversePpv != null ? Math.abs(meta.transversePpv) : 0,
      ),
      notes: [meta?.note1, meta?.note2, meta?.note3].filter(Boolean).join(' '),
      note1: meta?.note1 ?? '',
      note2: meta?.note2 ?? '',
      note3: meta?.note3 ?? '',
      deviceLabel: meta?.seismographSerialNumber ?? meta?.seismographName ?? '-',
      controlPointName: controlPoint?.name ?? '',
      previews: [
        toPreviewDataUrl(meta?.channel1PreviewImage),
        toPreviewDataUrl(meta?.channel2PreviewImage),
        toPreviewDataUrl(meta?.channel3PreviewImage),
      ],
    };
  }

  private matchesSearch(row: VibrationRow, term: string): boolean {
    return [row.note1, row.note2, row.note3, row.deviceLabel, row.controlPointName, row.dateLabel, row.timeLabel]
      .some(v => v?.toLowerCase().includes(term));
  }

  private formatTimestamp(ts?: string): [string, string] {
    const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(ts ?? '');
    if (!parts) return ['-', '-'];
    const [, year, month, day, hours, minutes, seconds] = parts;
    return [`${day}/${month}/${year}`, `${hours}:${minutes}:${seconds ?? '00'}`];
  }

  private pascalToDecibel(pressure: number): number {
    const referencePressure = 20e-6;
    return 20 * Math.log10(Math.abs(pressure) / referencePressure);
  }

  // ── Channel (colonne "Channel N ‹ ›", global à toute la liste) ─────────────

  protected channelCount(row: VibrationRow): number {
    const count = row.record.signalDataEntity?.signalData?.channels?.length
      || row.record.recordMetadata?.transducerAxis?.length
      || 3;
    return Math.min(count, MAX_CHANNELS);
  }

  protected cycleChannel(dir: number): void {
    const max = this.maxChannelCount();
    this.selectedChannelIndex.set((this.selectedChannelIndex() + dir + max) % max);
  }

  protected previewFor(row: VibrationRow, channelIndex: number): string | null {
    return row.previews[channelIndex] ?? null;
  }

  protected sparklineFor(row: VibrationRow, channelIndex: number): VibrationSparkline | null {
    const channel = row.record.signalDataEntity?.signalData?.channels?.[channelIndex];
    const raw = channel?.timeSpectrum?.values;
    if (!channel || !raw?.length) return null;

    const max = channel.timeSpectrum.ppvMax;
    const min = channel.timeSpectrum.ppvMin;
    const bound = Math.max(Math.abs(max), Math.abs(min)) || 1;

    const width = 120;
    const height = 32;
    const step = raw.length > 60 ? Math.floor(raw.length / 60) : 1;
    const sampled: number[] = [];
    for (let i = 0; i < raw.length; i += step) sampled.push(raw[i] as number);

    const points = sampled
      .map((v, i) => {
        const x = (i / (sampled.length - 1 || 1)) * width;
        const y = height / 2 - (v / bound) * (height / 2 - 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

    const samplingFrequency = row.record.recordMetadata?.samplingPeriod;
    const totalSeconds = samplingFrequency ? raw.length / samplingFrequency : 0;
    const ticks = [0, totalSeconds / 3, (totalSeconds / 3) * 2, totalSeconds].map(s => `${s.toFixed(0)}s`);

    return { points, max, min, ticks };
  }

  // ── Date navigation ───────────────────────────────────────────────────────

  protected goToday(): void {
    this.selectedDate.set(this.formatDate(new Date()));
  }

  protected goPrev(): void {
    this.shiftDate(-1);
  }

  protected goNext(): void {
    this.shiftDate(1);
  }

  protected onDateChange(value: string | null): void {
    this.selectedDate.set(value || null);
  }

  protected clearDateFilter(): void {
    this.selectedDate.set(null);
  }

  private shiftDate(days: number): void {
    const d = this.parseDate(this.selectedDate() ?? this.formatDate(new Date()));
    d.setDate(d.getDate() + days);
    this.selectedDate.set(this.formatDate(d));
  }

  private formatDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private parseDate(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }

  // ── Filtres / onglets / recherche ─────────────────────────────────────────

  protected onAssignmentFilterSelected(option: LibSelectOption): void {
    this.assignmentFilter.set((option.id as AssignmentFilter) ?? 'all');
  }

  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  protected onTabClick(controlPointId: number | null): void {
    if (controlPointId === null) {
      this.activeControlPointId.set(null);
      return;
    }
    this.activeControlPointId.set(this.activeControlPointId() === controlPointId ? null : controlPointId);
  }

  protected scrollTabs(dir: number): void {
    this.tabsTrack?.nativeElement.scrollBy({ left: dir * 220, behavior: 'smooth' });
  }

  // ── Sélection ─────────────────────────────────────────────────────────────

  protected isSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  protected toggleSelect(id: number): void {
    const next = new Set(this.selectedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selectedIds.set(next);
  }

  protected toggleSelectAll(): void {
    if (this.allSelected()) {
      this.selectedIds.set(new Set());
      return;
    }
    this.selectedIds.set(new Set(this.rows().map(r => r.id)));
  }

  // ── Actions groupées ──────────────────────────────────────────────────────

  protected openAddRecord(): void {
    if (!this.siteId()) return;
    this.addRecordOpen.set(true);
  }

  protected closeAddRecord(): void {
    this.addRecordOpen.set(false);
  }

  protected openMultiAssign(): void {
    this.multiAssignOpen.set(true);
  }

  protected closeMultiAssign(): void {
    this.multiAssignOpen.set(false);
  }

  protected downloadFile(row: VibrationRow): void {
    const meta = row.record.recordMetadata;
    if (!meta?.seismographSerialNumber || !meta?.fileName) return;
    const s3key = `Vibrations/${meta.seismographSerialNumber.toUpperCase()}/${meta.fileName}`;
    this.vibrationsRecordService.getDownloadUrl(s3key).subscribe({
      next: (result) => {
        if (result?.downloadUrl) window.open(result.downloadUrl, '_blank');
      },
    });
  }

  protected bulkDownload(): void {
    this.selectedRows().forEach(row => this.downloadFile(row));
  }

  protected downloadReport(row: VibrationRow): void {
    const language = this.cookieService.get('epcLanguage') || 'en';
    this.vibrationsRecordService.downloadRecordReport(row.record.id, language, this.tirName(), this.siteName(), this.siteId()).subscribe({
      next: (response) => {
        const blob = response.body ?? new Blob([], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `vibration_record_${row.record.id}_report.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        setTimeout(() => {
          anchor.remove();
          window.URL.revokeObjectURL(url);
        }, 100);
      },
    });
  }

  protected bulkReport(): void {
    const row = this.selectedRows()[0];
    if (row) this.downloadReport(row);
  }

  protected askDeleteSingle(row: VibrationRow): void {
    this.pendingDeleteIds.set([row.id]);
  }

  protected askBulkDelete(): void {
    if (!this.selectedCount()) return;
    this.pendingDeleteIds.set(Array.from(this.selectedIds()));
  }

  protected cancelDelete(): void {
    this.pendingDeleteIds.set(null);
  }

  protected async confirmDelete(): Promise<void> {
    const ids = this.pendingDeleteIds();
    const siteId = this.siteId();
    if (!ids || !siteId) return;
    try {
      if (ids.length === 1) {
        await this.vibrationsRecordService.deleteRecords(siteId, ids[0].toString());
      } else {
        await this.vibrationsRecordService.deleteRecordsBulk(siteId, ids);
      }
      this.records.set(this.records().filter(r => !ids.includes(r.id)));
      const next = new Set(this.selectedIds());
      ids.forEach(id => next.delete(id));
      this.selectedIds.set(next);
      if (this.expandedRowId() && ids.includes(this.expandedRowId()!)) this.expandedRowId.set(null);
      this.fonctionsService.launch_toast(true);
    } catch {
      this.fonctionsService.launch_toast(false);
    } finally {
      this.pendingDeleteIds.set(null);
    }
  }

  // ── Notes ─────────────────────────────────────────────────────────────────

  protected openNotes(row: VibrationRow): void {
    this.noteEditRowId.set(row.id);
    this.noteDraft.set({ note1: row.note1, note2: row.note2, note3: row.note3 });
  }

  protected closeNotes(): void {
    this.noteEditRowId.set(null);
  }

  protected updateNoteDraft(field: 'note1' | 'note2' | 'note3', value: string): void {
    this.noteDraft.set({ ...this.noteDraft(), [field]: value });
  }

  protected saveNotes(): void {
    const id = this.noteEditRowId();
    const siteId = this.siteId();
    const record = this.records().find(r => r.id === id);
    if (!record || !siteId) return;
    const draft = this.noteDraft();
    record.recordMetadata.note1 = draft.note1;
    record.recordMetadata.note2 = draft.note2;
    record.recordMetadata.note3 = draft.note3;
    this.persist(record, () => this.closeNotes());
  }

  // ── Distance ──────────────────────────────────────────────────────────────

  protected openDistanceEdit(row: VibrationRow): void {
    this.distanceEditRowId.set(row.id);
    this.distanceDraft.set(row.record.distance ?? null);
  }

  protected closeDistanceEdit(): void {
    this.distanceEditRowId.set(null);
  }

  protected updateDistanceDraft(value: string): void {
    const n = Number(value);
    this.distanceDraft.set(Number.isFinite(n) ? n : null);
  }

  protected saveDistance(): void {
    const id = this.distanceEditRowId();
    const record = this.records().find(r => r.id === id);
    if (!record) return;
    record.distance = this.distanceDraft() ?? undefined;
    this.persist(record, () => this.closeDistanceEdit());
  }

  private persist(record: VibrationData, onSuccess: () => void): void {
    const siteId = this.siteId();
    if (!siteId) return;
    const payload: VibrationRecord = {
      information: { currentURL: window.location.href, regionName: '', siteName: this.siteName(), controlPointName: '' },
      vibrationRecord: record,
    };
    this.vibrationsRecordService.updateRecord(siteId, record.id.toString(), payload).subscribe({
      next: () => {
        this.records.set([...this.records()]);
        this.fonctionsService.launch_toast(true);
        onSuccess();
      },
      error: () => this.fonctionsService.launch_toast(false),
    });
  }

  // ── Détail (ligne dépliée) ────────────────────────────────────────────────

  protected toggleExpand(row: VibrationRow): void {
    if (this.expandedRowId() === row.id) {
      this.expandedRowId.set(null);
      return;
    }
    this.expandedRowId.set(row.id);
    this.ensureSignalLoaded(row.record);
  }

  private ensureSignalLoaded(record: VibrationData): void {
    const siteId = this.siteId();
    if (!siteId || record.signalDataEntity) return;
    this.vibrationsRecordService.GetRecordById(siteId, record.id).subscribe({
      next: (response) => {
        record.signalDataEntity = response.data.signalDataEntity;
        record.timeSpectrumWeighted = response.data.timeSpectrumWeighted;
        this.records.set([...this.records()]);
      },
    });
  }

  protected viewInVibraData(row: VibrationRow): void {
    const siteId = this.siteId();
    if (!siteId) return;
    const url = `${this.vibradataUrl}/vibration-detail?deviceId=${row.record.recordMetadata?.seismographSerialNumber}&siteId=${siteId}&recordId=${row.id}`;
    window.open(url, '_blank');
  }

  protected unassignFromBlast(row: VibrationRow): void {
    const siteId = this.siteId();
    if (!siteId) return;
    row.record.blastId = 0;
    this.persist(row.record, () => {
      this.records.set(this.records().filter(r => r.id !== row.id));
      if (this.expandedRowId() === row.id) this.expandedRowId.set(null);
    });
  }
}
